import { AlertTriangle, CheckCircle } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'

interface ErrorEntry {
  type: string
  globalKeyword: { keyword: string } | null
  details: Record<string, unknown> | string | null
  createdAt: string
}

function formatDetails(details: ErrorEntry['details'], fallback: string): string {
  if (details == null) return fallback
  if (typeof details === 'string') return details
  if (typeof details.error === 'string') return details.error
  return JSON.stringify(details)
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'a l\'instant'
  if (mins < 60) return `il y a ${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `il y a ${hours}h`
  const days = Math.floor(hours / 24)
  return `il y a ${days}j`
}

export default function GlobalDbErrors() {
  const { data, loading } = useApi<ErrorEntry[]>('/api/admin/global-db/errors?limit=20')
  const entries = asArray(data)

  return (
    <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
      <h2 className="text-lg font-semibold text-[#1B2559] mb-4">Erreurs</h2>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-[#E2E4ED] rounded h-6 w-full" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle size={32} strokeWidth={1.25} className="text-[#10B981] mb-2" />
          <p className="text-sm text-[#8B8FA7]">Aucune erreur</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-3 pr-1">
          {entries.map((entry, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <AlertTriangle size={16} strokeWidth={1.25} className="text-[#EF4444] shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                {entry.globalKeyword?.keyword && (
                  <span className="font-medium text-[#1B2559]">{entry.globalKeyword.keyword} — </span>
                )}
                <span className="text-[#8B8FA7]">{formatDetails(entry.details, entry.type)}</span>
              </div>
              <span className="text-[#8B8FA7] shrink-0 text-xs">
                {timeAgo(entry.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
